<script lang="ts" setup>
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const { locale, locales } = useI18n();

// Store Flowbite instance for proper cleanup
let flowbiteInstance = null;
let exponeaNotificationModule = null;

declare global {
  interface Window {
    $BV: any;
    bvCallback: (...args: any[]) => any;
    exponea: any;
    monetateQ: any;
    Trustpilot: any;
    gtag: any;
    OneTrust: any;
    flowbiteInstances: any;
  }
}

useHead({
  link: generateHreflangLinks(
      route.fullPath,
      runtimeConfig.public.siteUrl,
      locale.value,
      locales.value
  ),
});

const TsSeoMetaTags = defineAsyncComponent(
    () => import("~/components/TsSeoMetaTags.vue")
);

const TsModalEcomError = defineAsyncComponent(
    () => import("~/components/TsModalEcomError.vue")
);

const rootStore = useRootStore();

/**
 * Manually trigger exponea tracking and push notification script by calling `load()` when user consents tracking cookies
 */
const exponeaScriptSource =
    runtimeConfig.public.assetBaseUrl + "/static/exp.js";

const exponeaScript = useExponeaScript(exponeaScriptSource, {
  trigger: exponeaTrackingConsent,
});

exponeaScript?.onLoaded(() => {
  import("~/services-optimized/exponea-notification-service").then(
      ({ checkAvailbility }) => {
        checkAvailbility();
        exponeaNotificationModule = import("~/services-optimized/exponea-notification-service");
      }
  );
});

async function loadTrolley() {
  const CheckoutPaths = ["/trolley", "/checkout"];

  const onCheckout = CheckoutPaths.some((path) =>
      route.fullPath.includes(path)
  );

  /* Set the guest/customer trolley resources only when the header cart link is present in the DOM,
     This is to prevent trolley being loaded on "trolley"/"checkout"/"404" pages and reduce getProducts API calls */
  const headerCartLink = document.getElementById("header-cart-link");
  if (!headerCartLink || onCheckout) return;

  try {
    const { useTrolleyStore } = await import("~/stores/trolley.store");
    const trolleyStore = useTrolleyStore();
    /* Set guest or customer trolley when app loads */
    trolleyStore.loadDefaultTrolley();
  } catch (error) {
    console.error("Failed to load trolley:", error);
  }
}

onMounted(async () => {
  if (rootStore.debugPanel) {
    runtimeConfig.public.appDebug = true;
  }

  // Awaiting for dynamic store import
  await loadTrolley();

  const { getBrowser } = await import("~/utils/dom.utils");
  rootStore.browser = getBrowser();

  const { initFlowbite } = await import("flowbite");
  nextTick(() => {
    flowbiteInstance = initFlowbite();
    rootStore.isAppHydrated = true;
    // logStorageUsage(); // To view local and cookie storage consumption to monitor app health
  });
});

onUnmounted(() => {
  const interval_id = rootStore.access_token_interval_id;
  /* clear the set interval from plugin [refresh-access-token] */
  if (interval_id) clearInterval(interval_id);

  // Clean up exponea script event listeners
  if (exponeaScript && typeof exponeaScript.cleanup === 'function') {
    exponeaScript.cleanup();
  }

  // Clean up any Flowbite initializations
  if (flowbiteInstance) {
    flowbiteInstance.destroy();
  }

  // Clean up exponea notification service module
  if (exponeaNotificationModule) {
    exponeaNotificationModule.then(module => {
      if (module && typeof module.cleanup === 'function') {
        module.cleanup();
      }
    }).catch(() => {
      // Ignore errors during cleanup
    });
    exponeaNotificationModule = null;
  }
});
</script>

<template>
  <div>
    <DebugPanel v-if="rootStore.debugPanel" />
    <TsSeoMetaTags />
    <NuxtLoadingIndicator />
    <NuxtLayout>
      <TsModalEcomError v-if="rootStore.debugPanel" />
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>
